import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { executiveAssignmentsApi, authApi } from '../api/services';
import { setActivePrincipalId as setClientPrincipalId } from '../api/client';
import type { ExecutiveAssignment } from '../types/models';
// Storage lives in ./principalStorage (JSON envelope, single owner of the
// session key) so AuthProvider can clear it on sign-out without a circular
// import. Storage is read ONCE per mount (readStoredChoice at init);
// afterwards the in-memory state/refs are the source of truth and storage
// is write-only persistence.
import { readStoredChoice, writeStoredChoice, clearStoredPrincipal, type StoredChoice } from './principalStorage';

export interface Principal {
  id: string;
  name: string;
  email: string;
  isTenantAdmin: boolean;
}

interface PrincipalContextType {
  /** The principal the EA is currently acting on behalf of (null = own view) */
  activePrincipal: Principal | null;
  /** Set the active principal (pass null to clear) */
  setActivePrincipal: (principal: Principal | null) => void;
  /** Clear principal selection back to "My View" */
  clearPrincipal: () => void;
  /** List of assigned executives */
  principals: Principal[];
  /** Whether the EA is currently acting on behalf of a principal */
  isActingOnBehalf: boolean;
  /** Whether principal list is loading */
  loading: boolean;
  /** The list couldn't be read (it retries twice by itself): not "no executives". */
  loadFailed: boolean;
  /** Read the list again, after a failure. */
  reload: () => void;
}

const PrincipalContext = createContext<PrincipalContextType | null>(null);

export function usePrincipal() {
  const context = useContext(PrincipalContext);
  if (!context) throw new Error('usePrincipal must be used within PrincipalContextProvider');
  return context;
}

export function PrincipalContextProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isEa = user?.role === 'ExecutiveAssistant';

  // Single storage read per mount; everything after runs off memory.
  const initialChoiceRef = useRef<StoredChoice | null | undefined>(undefined);
  if (initialChoiceRef.current === undefined) {
    initialChoiceRef.current = isEa ? readStoredChoice() : null;
  }

  const [principals, setPrincipals] = useState<Principal[]>([]);
  const [activePrincipal, setActivePrincipalState] = useState<Principal | null>(() => {
    const choice = initialChoiceRef.current;
    if (choice && 'principal' in choice) {
      // Immediately sync to API client on init
      setClientPrincipalId(choice.principal.id);
      return choice.principal;
    }
    return null;
  });
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloads, setReloads] = useState(0);

  // The EA's deliberate "My View" choice — blocks the sole-principal
  // auto-select. Seeded from storage, updated by setActivePrincipal.
  const myViewChosenRef = useRef<boolean>(initialChoiceRef.current !== null && 'myView' in (initialChoiceRef.current as object));

  // Always-current mirror of activePrincipal so the async load below never
  // validates against a stale closure.
  const activePrincipalRef = useRef<Principal | null>(activePrincipal);
  useEffect(() => { activePrincipalRef.current = activePrincipal; });

  // Set once a load SUCCEEDS for this user id — a failed load leaves it
  // unset so the effect (or the scheduled retry) can try again.
  const loadedForUserRef = useRef<string | null>(null);

  const setActivePrincipal = useCallback((principal: Principal | null) => {
    setActivePrincipalState(principal);
    if (principal) {
      myViewChosenRef.current = false;
      writeStoredChoice({ principal });
      setClientPrincipalId(principal.id);
    } else {
      // Record the DELIBERATE "My View" choice so the sole-principal
      // auto-select doesn't re-apply it on the next load.
      myViewChosenRef.current = true;
      writeStoredChoice({ myView: true });
      setClientPrincipalId(null);
    }
  }, []);

  const clearPrincipal = useCallback(() => {
    setActivePrincipal(null);
  }, [setActivePrincipal]);

  // Load executive assignments when user is an EA. The retry timer is owned
  // by the effect: logout / user-switch / unmount runs the cleanup, so no
  // timer can fire against a dead session or a different user (the old code
  // leaked the timer and its stale closures).
  useEffect(() => {
    if (!isEa || !user) {
      setPrincipals([]);
      // Allow a full reload if the same user signs back in later.
      loadedForUserRef.current = null;
      return;
    }
    if (loadedForUserRef.current === user.id) return;

    let cancelled = false;
    let retryTimer: number | undefined;

    const load = async (attempt: number) => {
      setLoading(true);
      setLoadFailed(false);
      try {
        const [assignments, users] = await Promise.all([
          executiveAssignmentsApi.getAll(),
          authApi.getUsers(),
        ]);
        if (cancelled) return;

        const activeAssignments = assignments.filter((a: ExecutiveAssignment) => a.isActive);
        const principalList: Principal[] = activeAssignments.map((a: ExecutiveAssignment) => {
          const u = users.find((x: any) => x.id === a.executiveUserId);
          return {
            id: a.executiveUserId,
            name: u?.displayName || 'Unknown Executive',
            email: u?.email || '',
            isTenantAdmin: u?.isTenantAdmin || false,
          };
        });

        setPrincipals(principalList);
        loadedForUserRef.current = user.id; // success only
        setLoadFailed(false);

        // Validate any restored selection against the live assignment list; a
        // stale one (assignment revoked) is cleared and then treated as "no
        // selection", so the sole-principal auto-select below can still apply
        // — e.g. an EA reassigned from exec X to exec Y lands on Y, matching
        // what a fresh session would do.
        let current = activePrincipalRef.current;
        if (current && !principalList.some(p => p.id === current!.id)) {
          setActivePrincipalState(null);
          setClientPrincipalId(null);
          clearStoredPrincipal();
          current = null;
        }
        if (!current && !myViewChosenRef.current && principalList.length === 1) {
          // Sole-principal convenience, made VISIBLE: an EA with exactly one
          // executive lands in that executive's view by explicit selection
          // (dropdown + acting-on-behalf banner show it) instead of the old
          // server-side silent default that mislabelled it "My View". A
          // deliberate "My View" choice is never overridden. The server still
          // verifies the assignment on every request — this selection grants
          // nothing by itself.
          const sole = principalList[0];
          setActivePrincipalState(sole);
          setClientPrincipalId(sole.id);
          writeStoredChoice({ principal: sole });
        }
      } catch {
        // Transient failure (network blip, token refresh race). The old code
        // silently set [] and never retried, so the switcher vanished for the
        // whole session (Rosie, 2026-08-26). Keep whatever list we had and
        // retry twice with backoff.
        if (!cancelled) setLoadFailed(true);
        if (!cancelled && attempt < 2) {
          retryTimer = window.setTimeout(() => { void load(attempt + 1); }, 8000 * (attempt + 1));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load(0);
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    };
  }, [isEa, user, reloads]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useCallback(() => {
    loadedForUserRef.current = null;
    setReloads(n => n + 1);
  }, []);

  // Clear principal context when user is not EA
  useEffect(() => {
    if (!isEa) {
      setActivePrincipalState(null);
      setClientPrincipalId(null);
      myViewChosenRef.current = false;
      clearStoredPrincipal();
    }
  }, [isEa]);

  const value: PrincipalContextType = {
    activePrincipal,
    setActivePrincipal,
    clearPrincipal,
    principals,
    isActingOnBehalf: activePrincipal !== null,
    loading,
    loadFailed,
    reload,
  };

  return (
    <PrincipalContext.Provider value={value}>
      {children}
    </PrincipalContext.Provider>
  );
}
